import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { EyeIcon } from '../../../components/icons/Icons';
import ApprovalDetailModal from '../../procurement/components/ApprovalDetailModal'; 

// This component displays a searchable and sortable list of all purchase requests.
export default function PurchaseCenter({ userProfile, appId }) {
    const [allRequests, setAllRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'descending' });

    // --- STEP 1: Add state for pagination ---
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    useEffect(() => {
        if (!userProfile) {
            setIsLoading(false);
            return;
        }

        const requestsCollectionPath = `/artifacts/${appId}/public/data/purchaseRequests`;
        const q = query(collection(db, requestsCollectionPath));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setAllRequests(snapshot.docs.map(d => ({ id: d.id, ...d.data(), type: 'Purchase' })));
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [appId, userProfile]);

    const handleViewRequest = (request) => {
        setSelectedRequest(request);
        setIsModalOpen(true);
    };

    const processedRequests = useMemo(() => {
        let filtered = [...allRequests];

        if (searchTerm) {
            const lowercasedTerm = searchTerm.toLowerCase();
            // Reset to page 1 whenever a search is performed
            setCurrentPage(1); 
            filtered = filtered.filter(req => 
                req.requestNumber?.toLowerCase().includes(lowercasedTerm) ||
                (req.vendorType === 'new' ? req.newVendorName : req.selectedVendor)?.toLowerCase().includes(lowercasedTerm) ||
                req.requesterName?.toLowerCase().includes(lowercasedTerm) ||
                req.department?.toLowerCase().includes(lowercasedTerm)
            );
        }

        filtered.sort((a, b) => {
            const getSortValue = (req, key) => {
                switch(key) {
                    case 'vendor': return req.vendorType === 'new' ? req.newVendorName : req.selectedVendor;
                    case 'lastUpdated': return req.updatedAt?.toDate() || req.createdAt?.toDate() || new Date(0);
                    default: return req[key] || '';
                }
            };
            
            let aValue = getSortValue(a, sortConfig.key);
            let bValue = getSortValue(b, sortConfig.key);

            if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
        });

        return filtered;
    }, [allRequests, searchTerm, sortConfig]);
    
    // --- STEP 2: Calculate the data for the current page ---
    const paginatedRequests = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return processedRequests.slice(startIndex, endIndex);
    }, [processedRequests, currentPage, itemsPerPage]);

    // --- STEP 3: Create handlers for pagination controls ---
    const totalPages = Math.ceil(processedRequests.length / itemsPerPage);

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };
    
    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(Number(event.target.value));
        setCurrentPage(1); // Reset to first page
    };

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
        setCurrentPage(1); // Reset to first page on sort
    };

    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'Pending Manager Approval': return 'bg-yellow-100 text-yellow-800';
            case 'Approved':
            case 'Approved for Payment':
                return 'bg-green-100 text-green-800';
            case 'Rejected':
            case 'Rejected by Finance':
                return 'bg-red-100 text-red-800';
            case 'Paid': return 'bg-blue-100 text-blue-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (isLoading) return <LoadingSpinner />;

    return (
        <>
            {isModalOpen && (
                <ApprovalDetailModal 
                    item={selectedRequest}
                    onClose={() => setIsModalOpen(false)}
                    appId={appId}
                />
            )}
            <div>
                <h2 className="text-2xl font-semibold text-[#213368] mb-4">Purchase Center</h2>
                <div className="mb-4">
                    <input
                        type="text"
                        placeholder="Search by Purchase ID, Vendor, Requester, or Department..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-lg shadow-sm"
                    />
                </div>
                <div className="bg-white shadow-md rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 table-fixed">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th onClick={() => requestSort('requestNumber')} className="w-1/6 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Purchase ID</th>
                                    <th onClick={() => requestSort('vendor')} className="w-2/6 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Vendor</th>
                                    <th onClick={() => requestSort('billingEntity')} className="w-1/6 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Billing Entity</th>
                                    <th onClick={() => requestSort('department')} className="w-1/6 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Department</th>
                                    <th onClick={() => requestSort('status')} className="w-1/6 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Status</th>
                                    <th onClick={() => requestSort('lastUpdated')} className="w-1/6 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Last Updated</th>
                                    <th className="w-auto px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {/* --- STEP 4: Render the paginated data --- */}
                                {paginatedRequests.length > 0 ? paginatedRequests.map(req => (
                                    <tr key={req.id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono truncate">{req.requestNumber}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm truncate">{req.vendorType === 'new' ? req.newVendorName : req.selectedVendor}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm truncate">{req.billingEntity || 'N/A'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm truncate">{req.department}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(req.status)}`}>
                                                {req.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            {(req.updatedAt || req.createdAt)?.toDate().toLocaleDateString() || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <button onClick={() => handleViewRequest(req)} className="p-2 text-gray-500 hover:text-[#20BEC6]" title="View Details">
                                                <EyeIcon />
                                            </button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="7" className="text-center py-8 text-gray-500">No purchase requests found.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* --- STEP 5: Add pagination controls --- */}
                {totalPages > 1 && (
                    <div className="flex justify-between items-center mt-4">
                        <div className="flex items-center space-x-2 text-sm">
                            <span>Items per page:</span>
                            <select value={itemsPerPage} onChange={handleItemsPerPageChange} className="border border-gray-300 rounded-md p-1">
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                        <div className="flex items-center space-x-2">
                            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border rounded-md disabled:opacity-50">
                                Previous
                            </button>
                            <span className="text-sm">
                                Page {currentPage} of {totalPages}
                            </span>
                            <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 border rounded-md disabled:opacity-50">
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}